import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import { DrawLayerStateManager } from '#nora-ui/map/map/state-managers/DrawStateManager';
import { FileStateManager } from '#nora-ui/map/map/state-managers/FileStateManager';
import { GeoSearchStateManager } from '#nora-ui/map/map/state-managers/GeoSearchStateManager';
import { MarkerStateManager } from '#nora-ui/map/map/state-managers/MarkerStateManager';
import { NotificationStateManager } from '#nora-ui/map/map/state-managers/NotificationStateManager';
import { TrackStateManager } from '#nora-ui/map/map/state-managers/TrackStateManager';

interface StateManagerOperationScoped {
  loadOperation: (operationId: string) => Promise<void>;
  releaseOperation: (operationId: string) => void;
}

/**
 * Manages different types of resources associated with the map,
 * such as files, drawn features, markers, tracks, and notifications.
 */
export class StateManager {
  /**
   * Resource managers for different resource types
   */
  private fileStateManager: FileStateManager;
  private drawStateManager: DrawLayerStateManager;
  private markerLayerStateManager: MarkerStateManager;
  private trackStateManager: TrackStateManager;
  private notificationStateManager: NotificationStateManager;
  private geoSearchStateManager: GeoSearchStateManager;

  /**
   * The managers that hold per-operation data and participate in operation load/release
   */
  private readonly operationScopedManagers: StateManagerOperationScoped[];

  get file(): FileStateManager {
    return this.fileStateManager;
  }

  get draw(): DrawLayerStateManager {
    return this.drawStateManager;
  }

  get marker(): MarkerStateManager {
    return this.markerLayerStateManager;
  }

  get track(): TrackStateManager {
    return this.trackStateManager;
  }

  get notification(): NotificationStateManager {
    return this.notificationStateManager;
  }

  get geoSearch(): GeoSearchStateManager {
    return this.geoSearchStateManager;
  }

  /**
   * Creates an instance of StateManager.
   * @param map
   */
  constructor(map: NoraMap) {
    this.drawStateManager = new DrawLayerStateManager(map);
    this.fileStateManager = new FileStateManager(map);
    this.notificationStateManager = new NotificationStateManager(map);
    this.trackStateManager = new TrackStateManager(map);

    this.markerLayerStateManager = new MarkerStateManager(map);
    this.geoSearchStateManager = new GeoSearchStateManager(map);

    this.operationScopedManagers = [
      this.drawStateManager,
      this.fileStateManager,
      this.notificationStateManager,
      this.trackStateManager,
    ];
  }

  /**
   * Loads state for all state managers.
   * Coordinates loading across all managers using Promise.allSettled for independent error handling.
   * State managers that need to fetch data from the API will do so during load, and are responsible for updating their respective layers.
   */
  async load(): Promise<void> {
    this.markerLayerStateManager.load();

    const results = await Promise.allSettled([
      this.drawStateManager.load(),
      this.fileStateManager.load(),
      this.notificationStateManager.load(),
      this.trackStateManager.load(),
    ]);

    // Log any failures but don't throw - we want partial success
    results.forEach((result) => {
      if (result.status === 'rejected') {
        console.error(`[StateManager] Failed to load state:`, result.reason);
      }
    });
  }

  /**
   * Loads the data of a single operation across all managers
   */
  async loadOperation(operationId: string): Promise<void> {
    const results = await Promise.allSettled(
      this.operationScopedManagers.map(async manager => await manager.loadOperation(operationId)),
    );

    results.forEach((result) => {
      if (result.status === 'rejected') {
        console.error(`[StateManager] Failed to load operation ${operationId}:`, result.reason);
      }
    });
  }

  /**
   * Releases the data of a single operation across all managers
   */
  releaseOperation(operationId: string): void {
    for (const manager of this.operationScopedManagers) {
      manager.releaseOperation(operationId);
    }
  }

  /**
   * Unloads state for all state managers.
   * Clears all state to prepare for a fresh load or map teardown.
   */
  unload(): void {
    this.drawStateManager.unload();
    this.fileStateManager.unload();
    this.notificationStateManager.unload();
    this.markerLayerStateManager.unload();
    this.trackStateManager.unload();
  }

  /**
   * Cleans up all resource managers.
   */
  cleanup(): void {
    this.trackStateManager.cleanup();
    this.fileStateManager.cleanup();
    this.drawStateManager.cleanup();
    this.markerLayerStateManager.cleanup();
    this.notificationStateManager.cleanup();
  }
}
